import type { ArticleCritiqueReport } from './article-critique';

/**
 * A REVIEWER'S LIMIT IS NOT AN OPEN FINDING.
 *
 * The critique is shown a window of each source ("shown as its opening and the stretches around what the
 * article quotes"), and when the sentence it is asked about lies in the part it was not shown, it says so:
 * "the excerpt cuts off before ...", "this requires verification in the full document", "Verify that Form
 * AP-236 is the correct form". That is a statement about the reviewer's view of the SOURCE, not a fault in
 * the ARTICLE. Counted as an open finding it gated the article and asked a person to decide something no
 * edit could change. On the v6 run of the 27 imported ITC articles it was 7 of the 35 open findings
 * (35db91cd, 042e69dc x3, d1d5ad75, a9c87c60, c80ffb3b).
 *
 * The classification is code, from the finding's own reason and suggestion and the critique's limitation
 * lines, never from a prompt. It is deliberately conservative: a finding that ALSO says the article is wrong
 * ("the HCAD schedule explicitly includes an INDEX FACTOR column ... misrepresents the structure", "the
 * article omits the statutory exception") is a real finding even when the same reason mentions truncation,
 * and a finding whose remedy is an edit ("Revise to ...") is not a request to go and verify.
 *
 * A reviewer limit is shown with the review's limitations. It never counts toward the release gate and is
 * never handed to the automatic fixer.
 */

type Finding = ArticleCritiqueReport['findings'][number];

/** A sentence of a reason: split on a full stop, semicolon or question mark followed by a space. */
const SENTENCES = /(?<=[.;!?])\s+/;

/** A sentence that says the reviewer's view of the source was short. */
const SOURCE_GAP =
  /\b(?:truncat\w*|cuts?\s+off|cut\s+off|falls?\s+outside|(?:was|were|is|are)\s+not\s+(?:provided|supplied|shown|included|given)|not\s+(?:provided|supplied|shown)\s+in|(?:does|do|did)\s+not\s+(?:explicitly\s+|directly\s+|specifically\s+)?(?:contain|include|supply|provide|name|state|detail|discuss|show|define|mention|describe|verify|cover|address|set\s+out)|outside\s+(?:of\s+)?the\s+(?:provided|supplied|shown)|(?:no|without)\s+(?:text|excerpt|copy)\s+of)\b/i;
/** The subject of such a sentence is the material the reviewer was given. */
const NAMES_THE_SUPPLIED =
  /\b(?:provided|supplied|shown|given|retrieved|excerpts?|truncat\w*|source\s+(?:text|material|excerpts?)|full\s+(?:text|document|statute|standard|guide))\b/i;
/** "needs verification" in any of its wordings. */
const NEEDS_VERIFICATION =
  /\b(?:needs?|requires?|require)\s+(?:to\s+be\s+)?(?:further\s+|independent\s+)?verif\w+|\b(?:cannot|could\s+not|can't|unable\s+to|not\s+able\s+to)\s+(?:be\s+)?(?:independently\s+)?verif\w+|\bnot\s+(?:been\s+)?verified\b/i;
/** The finding's remedy is only "go and check": it names no edit. */
const VERIFY_ONLY_SUGGESTION =
  /^\s*(?:verify|confirm|check|validate|cross-?check|ensure\s+that)\b/i;
/** A sentence that says the ARTICLE is wrong, short or misleading. */
const ARTICLE_FAULT =
  /\b(?:incorrect(?:ly)?|misstat\w+|misrepresent\w+|mischaracteri[sz]\w+|contradict\w*|overstat\w+|understat\w+|inaccurate(?:ly)?|wrong(?:ly)?|false(?:ly)?|fabricat\w+|omit(?:s|ted|ting)?|leaves?\s+(?:out|off)|fails?\s+to|conflates?|confus\w+|overlooks?|neglects?|misses|undercuts?|disrupts?|disconnected|abrupt|overly|awkward|unclear|ambiguous)\b/i;
/** A critique limitation that says part of a source was not seen. */
const LIMITATION_GAP =
  /\b(?:truncat\w*|cuts?\s+off|not\s+provided|were\s+not\s+shown|could\s+not\s+(?:independently\s+)?verify|not\s+(?:in|within)\s+the\s+(?:supplied|provided)|was\s+not\s+provided|could\s+not\s+retrieve)\b/i;

const sentencesOf = (text: string): string[] =>
  text
    .replace(/\s+/g, ' ')
    .split(SENTENCES)
    .map((sentence) => sentence.trim())
    .filter(Boolean);

function isSourceGapSentence(sentence: string): boolean {
  return SOURCE_GAP.test(sentence) && NAMES_THE_SUPPLIED.test(sentence);
}

/**
 * True when the finding's substance is "the source text the reviewer was shown was cut off / did not
 * contain it / needs verification", not "the article says something wrong". `limitations` are the critique's
 * own limitation lines: they corroborate a finding whose reason names the gap but whose remedy is not
 * phrased as "verify".
 */
export function isReviewerLimitFinding(
  finding: Pick<Finding, 'reason' | 'suggestion'>,
  limitations: readonly string[] = [],
): boolean {
  const reasonSentences = sentencesOf(finding.reason);
  const gapSentences = reasonSentences.filter(isSourceGapSentence);
  if (!gapSentences.length) return false;
  // A sentence that blames the article, and is not itself one of the gap sentences, makes this a real finding.
  const blamesTheArticle = reasonSentences.some(
    (sentence) => !isSourceGapSentence(sentence) && ARTICLE_FAULT.test(sentence),
  );
  if (blamesTheArticle) return false;
  const asksOnlyForVerification = VERIFY_ONLY_SUGGESTION.test(finding.suggestion);
  const saysNeedsVerification = reasonSentences.some((sentence) =>
    NEEDS_VERIFICATION.test(sentence),
  );
  const corroborated = limitations.some((line) => LIMITATION_GAP.test(line));
  return asksOnlyForVerification || saysNeedsVerification || corroborated;
}

export interface CritiqueFindingPartition {
  /** Findings about the article, which a person (or the fixer) can act on. */
  actionable: Finding[];
  /** The reviewer could not see the source text these rest on. Shown as limitations; never gating. */
  limits: Finding[];
}

export function partitionCritiqueFindings(
  report: Pick<ArticleCritiqueReport, 'findings' | 'limitations'>,
): CritiqueFindingPartition {
  const actionable: Finding[] = [];
  const limits: Finding[] = [];
  for (const finding of report.findings)
    (isReviewerLimitFinding(finding, report.limitations) ? limits : actionable).push(finding);
  return { actionable, limits };
}

/** The finding keys (dimension, quote, suggestion) that are reviewer limits, so every counter agrees. */
export function reviewerLimitFindingKeys(
  report: Pick<ArticleCritiqueReport, 'findings' | 'limitations'>,
  keyOf: (finding: Finding) => string,
): ReadonlySet<string> {
  return new Set(partitionCritiqueFindings(report).limits.map(keyOf));
}

/** Why a limit finding is listed with the limitations, in a sentence for the operator. */
export const REVIEWER_LIMIT_NOTE =
  'The reviewer could not see the part of the source this rests on, so nothing here says the article is wrong. The claim check reads the cited law itself.';
